import React, { useEffect, useState } from 'react';
import { t } from '../i18n';
import { useSearchParams } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import { listenSites, submitTask, listenTeamSubmissions, listenTeam, listenSiteTasks, startImageUpload, onAuthChange } from '../firebase';
import Scoreboard from '../components/Scoreboard';

// Fix leaflet default icon path when using CRA
try {
  delete L.Icon.Default.prototype._getIconUrl;
  L.Icon.Default.mergeOptions({
    iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
    iconUrl: require('leaflet/dist/images/marker-icon.png'),
    shadowUrl: require('leaflet/dist/images/marker-shadow.png')
  });
} catch (e) {
  // ignore in environments that disallow require
}

// Pre-create colored marker icons (uses GitHub-hosted colored markers)
const ICON_BASE = 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/';
const ICONS = {
  blue: L.icon({ iconUrl: ICON_BASE + 'marker-icon-2x-blue.png', shadowUrl: require('leaflet/dist/images/marker-shadow.png'), iconSize: [25,41], iconAnchor: [12,41], popupAnchor: [1,-34], tooltipAnchor: [16,-28], shadowSize: [41,41] }),
  green: L.icon({ iconUrl: ICON_BASE + 'marker-icon-2x-green.png', shadowUrl: require('leaflet/dist/images/marker-shadow.png'), iconSize: [25,41], iconAnchor: [12,41], popupAnchor: [1,-34], tooltipAnchor: [16,-28], shadowSize: [41,41] }),
  red: L.icon({ iconUrl: ICON_BASE + 'marker-icon-2x-red.png', shadowUrl: require('leaflet/dist/images/marker-shadow.png'), iconSize: [25,41], iconAnchor: [12,41], popupAnchor: [1,-34], tooltipAnchor: [16,-28], shadowSize: [41,41] }),
  yellow: L.icon({ iconUrl: ICON_BASE + 'marker-icon-2x-yellow.png', shadowUrl: require('leaflet/dist/images/marker-shadow.png'), iconSize: [25,41], iconAnchor: [12,41], popupAnchor: [1,-34], tooltipAnchor: [16,-28], shadowSize: [41,41] })
};

export default function PlayerMap(){
  const [currentUser, setCurrentUser] = useState(null);
  const [locationStatus, setLocationStatus] = useState('idle'); // idle | sharing | denied | error
  const [lastLocation, setLastLocation] = useState(null);
  const [sites, setSites] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [team, setTeam] = useState(null);
  const [tasksMap, setTasksMap] = useState({});
  const [fileMap, setFileMap] = useState({});
  const [uploading, setUploading] = useState({});
  const [progressMap, setProgressMap] = useState({});
  const [searchParams] = useSearchParams();
  const teamId = searchParams.get('teamId') || (typeof window !== 'undefined' ? localStorage.getItem('teamId') : null);

  useEffect(()=>{
    const unsub = listenSites(setSites);
    return () => unsub();
  }, []);

  useEffect(()=>{
    const unsub = onAuthChange(user => setCurrentUser(user));
    return unsub;
  }, []);

  useEffect(()=>{
    // subscribe to tasks subcollections for each site
    const unsubs = sites.map(s => listenSiteTasks(s.id, (tasks) => {
      setTasksMap(prev => ({ ...prev, [s.id]: tasks }));
    }));
    return () => unsubs.forEach(u => u && u());
  }, [sites]);

  useEffect(()=>{
    if (!teamId) return;
    const unsub = listenTeamSubmissions(teamId, setSubmissions);
    return () => unsub();
  }, [teamId]);

  useEffect(()=>{
    if (!teamId) return;
    const unsub = listenTeam(teamId, setTeam);
    return () => unsub();
  }, [teamId]);

  // Geolocation updates: use watchPosition for continuous updates
  useEffect(()=>{
    // Geolocation is handled globally by LocationWatcher mounted in App.
    // Keep locationStatus UI but do not start a separate watcher here.
    if (!currentUser || !teamId) return;
    if (!navigator.geolocation) {
      setLocationStatus('error');
      return;
    }
    // try to infer sharing state from presence of lastLocation
    setLocationStatus(lastLocation ? 'sharing' : 'idle');
  }, [currentUser, teamId, team]);

  async function handleSubmitTask(siteId, taskId, textPayload){
    // prevent duplicate pending/approved submissions for this task by this team
    const already = submissions.find(s => s.siteId === siteId && s.taskId === taskId && (s.status === 'pending' || s.status === 'approved'));
    if (already) {
      alert(t('alreadySubmitted'));
      return;
    }

    // minimal payload example: include text if provided
    const payload = textPayload ? { text: textPayload } : { message: 'completed' };
  await submitTask({ teamId, siteId, taskId, payload });
  alert(t('taskSentForReview'));
  }

  // Compute an aggregate site status for this team (priority: approved -> pending -> rejected -> unfinished)
  // Per-task status for this team (approved/pending/rejected/unfinished)
  function getTaskStatus(siteId, task) {
    const subs = submissions.filter(s => s.siteId === siteId && s.taskId === task.id);
    if (subs.some(s => s.status === 'approved')) return 'approved';
    if (subs.some(s => s.status === 'pending')) return 'pending';
    if (subs.some(s => s.status === 'rejected')) return 'rejected';
    return 'unfinished';
  }

  // Compute an aggregate site status for icon choice (based on remaining tasks).
  // If all tasks are approved the site is considered completed (caller may hide the site).
  function getSiteStatus(site) {
    const tasks = tasksMap[site.id] || [];
    if (!tasks.length) return 'unfinished';
    // If all tasks approved => completed
    const allApproved = tasks.every(t => getTaskStatus(site.id, t) === 'approved');
    if (allApproved) return 'completed';

    // among remaining tasks, prefer pending -> rejected -> unfinished
    let foundPending = false;
    let foundRejected = false;
    for (const t of tasks) {
      const st = getTaskStatus(site.id, t);
      if (st === 'pending') foundPending = true;
      if (st === 'rejected') foundRejected = true;
    }
    if (foundPending) return 'pending';
    if (foundRejected) return 'rejected';
    return 'unfinished';
  }

  function iconForStatus(status) {
    switch(status) {
      case 'approved': return ICONS.green;
      case 'completed': return ICONS.green;
      case 'pending': return ICONS.yellow;
      case 'rejected': return ICONS.red;
      default: return ICONS.blue;
    }
  }

  const center = sites.length ? [sites[0].location.lat, sites[0].location.lng] : [59.437,24.7536]; // Tallinn default

  return (
    <div className="container py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="fw-bold">{t('playerMap')}</h2>
      </div>
      <div style={{height:'75vh'}}>
        <MapContainer center={center} zoom={13} style={{height: '100%'}}>
            <TileLayer
              attribution='&copy; OpenStreetMap contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />

            {sites.map(s => {
              // hide site entirely if all tasks are approved for this team
              const status = getSiteStatus(s);
              // also hide if team doc explicitly lists this site as completed
              const isCompletedFlag = team && Array.isArray(team.completedSites) && team.completedSites.includes(s.id);
              if (status === 'completed' || isCompletedFlag) return null;
              return (
                <Marker key={s.id} position={[s.location.lat, s.location.lng]} icon={iconForStatus(status)}>
                  <Popup>
                    <div className="p-2" style={{minWidth: 260}}>
                      <div className="d-flex flex-column">
                        <div className="mb-2">
                          <strong className="d-block">{s.title}</strong>
                          <div className="small text-muted">{s.description}</div>
                        </div>

                        <div>
                          <em>{t('tasks')}:</em>
                          <ul className="list-unstyled mt-2">
                            {(tasksMap[s.id] || []).filter(task => {
                              const st = getTaskStatus(s.id, task);
                              return st === 'unfinished'; // hide tasks that are pending or approved
                            }).map((task) => {
                              const hasApproved = submissions.some(sub => sub.siteId === s.id && sub.taskId === task.id && sub.status === 'approved');
                              const hasPending = submissions.some(sub => sub.siteId === s.id && sub.taskId === task.id && sub.status === 'pending');
                              return (
                                <li key={task.id} className="mb-3">
                                  <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start">
                                    <div className="mb-2 mb-sm-0">
                                      <div className="fw-semibold">{task.title} <span className="badge bg-info text-dark ms-2">{task.points ?? 10} pts</span></div>
                                      <div className="small text-muted">{task.description}</div>
                                    </div>
                                    <div className="ms-sm-3 text-sm-end">
                                      {hasApproved ? <span className="badge bg-success">{t('approved')}</span> : hasPending ? <span className="badge bg-warning text-dark">{t('pending')}</span> : (
                                        task.type === 'photo' ? (
                                          <div className="d-flex flex-column align-items-end">
                                            {fileMap[task.id] ? (
                                              <div className="d-flex align-items-center">
                                                <img src={URL.createObjectURL(fileMap[task.id])} alt="preview" className="img-thumbnail me-2" style={{width:80, height:80, objectFit:'cover'}} />
                                                <div style={{minWidth:140}}>
                                                  <div className="mb-1">
                                                    <div className="progress" style={{height:8}}>
                                                      <div className="progress-bar" role="progressbar" style={{width: `${progressMap[task.id] || 0}%`}} aria-valuenow={progressMap[task.id]||0} aria-valuemin="0" aria-valuemax="100"></div>
                                                    </div>
                                                  </div>
                                                  <div className="small text-muted">{Math.round(progressMap[task.id] || 0)}%</div>
                                                  <div className="mt-2">
                                                    <button className="btn btn-sm btn-primary" onClick={async ()=>{
                                                      const file = fileMap[task.id];
                                                      if (!file) { alert(t('selectPhoto')); return; }
                                                      setUploading(prev=>({ ...prev, [task.id]: true }));
                                                      try {
                                                        const { promise } = startImageUpload(file, teamId, s.id, task.id, pct => setProgressMap(prev=>({ ...prev, [task.id]: pct })));
                                                        const url = await promise;
                                                        await submitTask({ teamId, siteId: s.id, taskId: task.id, payload: { imageUrl: url } });
                                                        alert(t('photoSubmitted'));
                                                        setFileMap(prev=>({ ...prev, [task.id]: null }));
                                                      } catch (err) {
                                                        console.error('Upload failed:', err);
                                                        const msg = err && err.message ? err.message : JSON.stringify(err);
                                                        alert(t('uploadFailed') + ': ' + msg);
                                                      } finally {
                                                        setUploading(prev=>({ ...prev, [task.id]: false }));
                                                        setProgressMap(prev=>({ ...prev, [task.id]: 0 }));
                                                      }
                                                    }} disabled={uploading[task.id]}>{uploading[task.id] ? t('uploading') : t('submit')}</button>
                                                    <button className="btn btn-sm btn-outline-secondary ms-2" onClick={()=>{ setFileMap(prev=>({ ...prev, [task.id]: null })); setProgressMap(prev=>({ ...prev, [task.id]: 0 })); }}>{t('remove')}</button>
                                                  </div>
                                                </div>
                                              </div>
                                            ) : (
                                              <input className="form-control form-control-sm" type="file" accept="image/*" onChange={e=>setFileMap(prev=>({ ...prev, [task.id]: e.target.files[0] }))} />
                                            )}
                                          </div>
                                        ) : (
                                          <button className="btn btn-sm btn-outline-primary" onClick={()=>{
                                            const text = window.prompt(t('enterProofText'));
                                            if (text) handleSubmitTask(s.id, task.id, text);
                                          }}>{t('submit')}</button>
                                        )
                                      )}
                                    </div>
                                  </div>
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      </div>
                    </div>
                  </Popup>
                </Marker>
              );
            })}
          </MapContainer>
      </div>
    </div>
  );
}
